import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';

function ReturnOrderForm() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const invoiceId = searchParams.get('invoice_id');

    const [invoice, setInvoice] = useState<any>(null);
    const [formData, setFormData] = useState({
        invoice_id: invoiceId || '',
        customer_id: '',
        return_date: new Date().toISOString().split('T')[0],
        reason: 'damaged',
        refund_method: 'credit_note',
        notes: ''
    });
    const [returnType, setReturnType] = useState<'full' | 'partial'>('full');
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const [partialRefundAmount, setPartialRefundAmount] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (invoiceId) {
            loadInvoice();
        } else {
            setLoading(false);
        }
    }, [invoiceId]);

    const loadInvoice = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/invoices/${invoiceId}`);
            if (response.success) {
                setInvoice(response.data);
                setFormData(prev => ({
                    ...prev,
                    customer_id: response.data.customer_id
                }));
                // Initialize selected items with all invoice items
                setSelectedItems(response.data.items?.map((item: any) => ({
                    invoice_item_id: item.id,
                    product_id: item.product_id,
                    product_name: item.product_name,
                    product_variant_id: item.product_variant_id,
                    batch_id: item.batch_id,
                    product_unit_id: item.product_unit_id,
                    max_quantity: item.quantity,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    tax_rate: item.tax_rate || 0,
                    condition: 'resellable',
                    selected: true
                })) || []);
            } else {
                setError(response.message || 'Failed to load invoice');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (returnType === 'full') {
            setSelectedItems(prev => prev.map(item => ({
                ...item,
                selected: true,
                quantity: item.max_quantity
            })));
            setPartialRefundAmount(''); // Reset when switching to full
        }
    }, [returnType]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index: number, field: string, value: any) => {
        const updated = [...selectedItems];
        updated[index] = { ...updated[index], [field]: value };
        setSelectedItems(updated);
    };

    const toggleItemSelection = (index: number) => {
        const updated = [...selectedItems];
        updated[index].selected = !updated[index].selected;
        setSelectedItems(updated);
    };

    const calculateTotals = () => {
        const items = selectedItems.filter(item => item.selected);
        const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
        const taxAmount = items.reduce((sum, item) => {
            const itemTotal = item.quantity * item.unit_price;
            return sum + (itemTotal * (item.tax_rate / 100));
        }, 0);
        const total = subtotal + taxAmount;

        return { subtotal, taxAmount, total };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const itemsToReturn = selectedItems.filter(item => item.selected && item.quantity > 0);

        if (itemsToReturn.length === 0) {
            setError('Please select at least one item to return');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const { subtotal, taxAmount, total } = calculateTotals();
            const effectiveRefundAmount = (returnType === 'partial' && partialRefundAmount !== '')
                ? parseFloat(partialRefundAmount) || 0
                : total;

            const payload = {
                return_order: {
                    ...formData,
                    subtotal,
                    tax_amount: taxAmount,
                    total_amount: total,
                    refund_amount: effectiveRefundAmount,
                    return_items_attributes: itemsToReturn.map(item => ({
                        invoice_item_id: item.invoice_item_id,
                        product_id: item.product_id,
                        product_variant_id: item.product_variant_id,
                        batch_id: item.batch_id,
                        product_unit_id: item.product_unit_id,
                        quantity: item.quantity,
                        unit_price: item.unit_price,
                        tax_rate: item.tax_rate,
                        tax_amount: (item.quantity * item.unit_price * item.tax_rate) / 100,
                        total_amount: item.quantity * item.unit_price * (1 + item.tax_rate / 100),
                        condition: item.condition
                    }))
                }
            };

            const response = await api.post('/return_orders', payload);

            if (response.success) {
                navigate(`/returns/${response.data.id}`);
            } else {
                setError(response.message || response.errors?.join(', ') || 'Failed to create return');
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Loading invoice...</div>;
    }

    if (!invoiceId || !invoice) {
        return (
            <div className="mt-16 p-8 text-center">
                <h2 className="text-xl font-bold text-slate-800 mb-4">No Invoice Selected</h2>
                <p className="text-slate-500 mb-6">Please create a return from an invoice page.</p>
                <Link to="/invoices" className="text-indigo-600 hover:text-indigo-700 font-medium">
                    Go to Invoices
                </Link>
            </div>
        );
    }

    const totals = calculateTotals();

    return (
        <div className="mt-8 max-w-4xl mx-auto">
            <div className="mb-8">
                <div className="flex items-center gap-3">
                    <Link to={`/invoices/${invoiceId}`} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">Create Return</h1>
                </div>
                <p className="text-slate-500 mt-1 ml-11">
                    Return items from Invoice: <span className="font-medium text-slate-700">{invoice.invoice_number}</span>
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                    <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md">
                        <p className="text-sm text-rose-700">{error}</p>
                    </div>
                )}

                {/* Return Type Selection */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-bold text-slate-800 mb-4">Return Type</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className={`relative flex cursor-pointer rounded-xl border p-4 shadow-sm focus:outline-none ${returnType === 'full' ? 'border-indigo-600 bg-indigo-50/50 ring-2 ring-indigo-600' : 'border-slate-300 bg-white hover:bg-slate-50'}`}>
                            <input type="radio" name="return_type" value="full" className="sr-only" checked={returnType === 'full'} onChange={() => setReturnType('full')} />
                            <span className="flex flex-1">
                                <span className="flex flex-col">
                                    <span className="block text-sm font-bold text-slate-900">Full Return</span>
                                    <span className="mt-1 flex items-center text-sm text-slate-500">Return all items and quantities from the invoice.</span>
                                </span>
                            </span>
                            <svg className={`h-5 w-5 ${returnType === 'full' ? 'text-indigo-600' : 'text-transparent'}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                            </svg>
                        </label>
                        <label className={`relative flex cursor-pointer rounded-xl border p-4 shadow-sm focus:outline-none ${returnType === 'partial' ? 'border-indigo-600 bg-indigo-50/50 ring-2 ring-indigo-600' : 'border-slate-300 bg-white hover:bg-slate-50'}`}>
                            <input type="radio" name="return_type" value="partial" className="sr-only" checked={returnType === 'partial'} onChange={() => setReturnType('partial')} />
                            <span className="flex flex-1">
                                <span className="flex flex-col">
                                    <span className="block text-sm font-bold text-slate-900">Partial Return</span>
                                    <span className="mt-1 flex items-center text-sm text-slate-500">Select specific items and quantities to return.</span>
                                </span>
                            </span>
                            <svg className={`h-5 w-5 ${returnType === 'partial' ? 'text-indigo-600' : 'text-transparent'}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                            </svg>
                        </label>
                    </div>
                </div>

                {/* Return Details */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                    <h2 className="text-lg font-bold text-slate-800 mb-4">Return Details</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Return Date <span className="text-rose-500">*</span></label>
                            <input
                                type="date"
                                name="return_date"
                                value={formData.return_date}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Reason <span className="text-rose-500">*</span></label>
                            <select
                                name="reason"
                                value={formData.reason}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                                <option value="damaged">Damaged</option>
                                <option value="wrong_item">Wrong Item</option>
                                <option value="customer_request">Customer Request</option>
                                <option value="defective">Defective</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Refund Method <span className="text-rose-500">*</span></label>
                            <select
                                name="refund_method"
                                value={formData.refund_method}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                                <option value="credit_note">Credit Note</option>
                                <option value="cash">Cash</option>
                                <option value="bank_transfer">Bank Transfer</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Notes</label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                rows={3}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                placeholder="Additional notes..."
                            />
                        </div>
                    </div>
                </div>

                {/* Items to Return */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200">
                        <h2 className="text-lg font-bold text-slate-800">Select Items to Return</h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <input type="checkbox" className="w-4 h-4 rounded" disabled={returnType === 'full'} checked={selectedItems.every(i => i.selected)} onChange={() => {
                                            const allSelected = selectedItems.every(i => i.selected);
                                            setSelectedItems(selectedItems.map(i => ({ ...i, selected: !allSelected })));
                                        }} />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase">Product</th>
                                    <th className="px-6 py-3 text-center text-xs font-bold text-slate-400 uppercase">Qty</th>
                                    <th className="px-6 py-3 text-center text-xs font-bold text-slate-400 uppercase">Condition</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-400 uppercase">Price</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-400 uppercase">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {selectedItems.map((item, index) => (
                                    <tr key={index} className={item.selected ? 'bg-indigo-50/30' : ''}>
                                        <td className="px-4 py-4">
                                            <input
                                                type="checkbox"
                                                checked={item.selected}
                                                onChange={() => toggleItemSelection(index)}
                                                disabled={returnType === 'full'}
                                                className="w-4 h-4 rounded"
                                            />
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-slate-800">{item.product_name}</td>
                                        <td className="px-6 py-4">
                                            <input
                                                type="number"
                                                min="1"
                                                max={item.max_quantity}
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                                                disabled={!item.selected || returnType === 'full'}
                                                className="w-20 px-2 py-1 text-center border border-slate-200 rounded-lg"
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <select
                                                value={item.condition}
                                                onChange={(e) => handleItemChange(index, 'condition', e.target.value)}
                                                disabled={!item.selected}
                                                className="px-2 py-1 text-sm border border-slate-200 rounded-lg"
                                            >
                                                <option value="resellable">Resellable</option>
                                                <option value="damaged">Damaged</option>
                                                <option value="defective">Defective</option>
                                            </select>
                                        </td>
                                        <td className="px-6 py-4 text-right text-sm font-mono">₹{item.unit_price?.toLocaleString()}</td>
                                        <td className="px-6 py-4 text-right text-sm font-mono font-bold">
                                            ₹{(item.quantity * item.unit_price * (1 + item.tax_rate / 100)).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Totals */}
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                        <div className="max-w-sm ml-auto space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Subtotal:</span>
                                <span className="font-mono font-medium">₹{totals.subtotal.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Tax:</span>
                                <span className="font-mono font-medium">₹{totals.taxAmount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-sm border-t border-slate-200 pt-2">
                                <span className="text-slate-600">Calculated Total:</span>
                                <span className="font-mono font-medium">₹{totals.total.toLocaleString()}</span>
                            </div>
                            {returnType === 'partial' && (
                                <div className="pt-2 border-t border-dashed border-indigo-200">
                                    <label className="block text-xs font-black text-indigo-600 uppercase tracking-widest mb-1">
                                        Partial Refund Amount <span className="text-slate-400 font-normal normal-case">(override if less than full)</span>
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-sm">₹</span>
                                        <input
                                            type="number"
                                            min="0"
                                            max={totals.total}
                                            step="0.01"
                                            className="w-full pl-7 pr-3 py-2.5 rounded-xl border-2 border-indigo-300 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none text-right font-mono font-bold text-indigo-700 text-sm"
                                            placeholder={totals.total.toFixed(2)}
                                            value={partialRefundAmount}
                                            onChange={(e) => setPartialRefundAmount(e.target.value)}
                                        />
                                    </div>
                                    <p className="text-xs text-slate-400 mt-1">Leave blank to refund full calculated amount (₹{totals.total.toFixed(2)})</p>
                                </div>
                            )}
                            <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2">
                                <span>Refund Amount:</span>
                                <span className="font-mono text-emerald-600">
                                    ₹{(returnType === 'partial' && partialRefundAmount !== '' ? parseFloat(partialRefundAmount) || 0 : totals.total).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3">
                    <Link
                        to={`/invoices/${invoiceId}`}
                        className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        disabled={saving}
                        className={`px-8 py-2.5 bg-rose-600 text-white rounded-xl text-sm font-bold hover:bg-rose-700 transition-all shadow-lg ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {saving ? 'Processing...' : 'Create Return'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default ReturnOrderForm;
